import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip';
import { HelpCircleIcon, PlusIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import AccountGroup from '../../components/AccountGroup';
import Entity from '../../components/entity';
import PlaidLinkButton from '../../components/plaid/PlaidLinkButton';
import Widget from '../../components/Widget';
import { useFinancialAccounts, type FinancialAccount } from '../../hooks/queries/useFinancialAccounts';
import { useFilteredTransactions } from '../../hooks/useFilteredTransactions';
import { PATHS } from '../../paths';
import { useNavigateWithDateRange } from '../../providers/DateRangeProvider';
import { filterByColumn } from '../../providers/TransactionsProvider';
import { groupAccountsBySubtype, isSupportedFinancialAccount, supportedSubtypes } from '../../utils/accounts';

import type { FinancialAccountSubtype } from '../../__generated__/globalTypes';
import type { ColumnTable } from 'arquero';

interface IBaseAccountOverviewPaneProps {
  financialAccounts: FinancialAccount[];
  unsupportedFinancialAccounts: FinancialAccount[];
  transactions: ColumnTable | null;
  onAccountClick: (accountId: string) => void;
  loading: boolean;
}

export const BaseAccountOverviewPane = ({
  financialAccounts,
  unsupportedFinancialAccounts,
  transactions,
  onAccountClick,
  loading,
}: IBaseAccountOverviewPaneProps) => {
  const groupedFinancialAccounts = React.useMemo(() => groupAccountsBySubtype(financialAccounts), [financialAccounts]);

  // Map of account subtype to transactions for that subtype
  const transactionsByType = React.useMemo(() => {
    if (!transactions) {
      return {} as Record<FinancialAccountSubtype, ColumnTable | null>;
    }

    const result = {} as Record<FinancialAccountSubtype, ColumnTable | null>;

    for (const subtype of supportedSubtypes) {
      const accounts = groupedFinancialAccounts[subtype] || [];
      if (accounts.length === 0) {
        result[subtype] = null;
        continue;
      }

      // Get all account IDs for this subtype
      const accountIds = accounts.map((account) => account.id);

      // Filter transactions where financialAccountId is in the accountIds array
      const filteredTransactions = filterByColumn(transactions, 'financialAccountId', accountIds);

      result[subtype] = filteredTransactions;
    }

    return result;
  }, [transactions, groupedFinancialAccounts]);

  const addButton = (
    <PlaidLinkButton iconLeading={<PlusIcon className="text-button-primary-icon size-4.5" />}>
      Add account
    </PlaidLinkButton>
  );

  const accountsBody = (
    <div className="flex flex-col gap-6">
      {supportedSubtypes.map((subtype) => {
        const accounts = groupedFinancialAccounts[subtype] || [];
        if (accounts.length === 0) {
          return null;
        }

        return (
          <AccountGroup
            key={subtype}
            subtype={subtype}
            accounts={accounts}
            transactions={transactionsByType[subtype]}
            onAccountClick={onAccountClick}
          />
        );
      })}
      {unsupportedFinancialAccounts.length > 0 && (
        <Widget collapsible defaultCollapsed>
          <Widget.Header
            title={
              <Tooltip title="These are accounts that you've connected but are not currently supported.">
                <div className="flex items-center gap-2">
                  Unsupported accounts
                  <TooltipTrigger>
                    <HelpCircleIcon className="text-secondary size-4" />
                  </TooltipTrigger>
                </div>
              </Tooltip>
            }
          />
          <Widget.Body>
            {unsupportedFinancialAccounts.map((financialAccount) => (
              <Widget.Row key={financialAccount.id} label={financialAccount.name} />
            ))}
          </Widget.Body>
        </Widget>
      )}
    </div>
  );

  return (
    <Entity.Pane>
      <Entity.Header title="Accounts" trailingContent={addButton} />
      <Entity.Body
        primaryElement={
          <>
            {loading && <LoadingIndicator fullScreen className="h-[500px]" />}
            {!loading && financialAccounts.length === 0 && <div>No financial accounts found</div>}
            {!loading && financialAccounts.length > 0 && accountsBody}
          </>
        }
      />
    </Entity.Pane>
  );
};

const AccountOverviewPane = () => {
  const navigate = useNavigateWithDateRange();

  const { transactions, loading: transactionsLoading } = useFilteredTransactions({
    includePaired: true,
  });
  const { financialAccounts, loading } = useFinancialAccounts();

  const filteredFinancialAccounts = financialAccounts?.filter(isSupportedFinancialAccount) ?? [];
  const unsupportedFinancialAccounts =
    financialAccounts?.filter((account) => !isSupportedFinancialAccount(account)) ?? [];

  const handleAccountClick = (accountId: string) => navigate(PATHS.ACCOUNT(accountId));

  return (
    <BaseAccountOverviewPane
      financialAccounts={filteredFinancialAccounts}
      unsupportedFinancialAccounts={unsupportedFinancialAccounts}
      transactions={transactions ?? null}
      onAccountClick={handleAccountClick}
      loading={loading || transactionsLoading}
    />
  );
};

export default AccountOverviewPane;
